import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Text,
  Group,
  Box,
  Stack,
  Avatar,
  Button,
  Textarea,
  TextInput,
  ActionIcon,
  Flex,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  Calendar as CalendarIcon,
  Clock,
  User,
  Monitor,
  ArrowLeft,
  X,
} from 'react-feather';
import { participantPortalApi } from '../../../services/api/participantPortal.js';
import type {
  ParticipantOneOnOneCoach,
  ParticipantOneOnOneSession,
  CalendlyAvailableSlot,
} from '../../../services/api/participantPortal.js';
import { useAuthStore } from '../../../stores/auth.js';
import { buildOneOnOneSessionTitle } from '../../../config/oneOnOneTypeConfig.js';
import { AvailabilityFailureAlert } from '../../../components/scheduling/AvailabilityFailureAlert.js';
import { SlotPicker } from '../../../components/scheduling/SlotPicker.js';
import { useMonthAvailability } from '../../../components/scheduling/useMonthAvailability.js';
import { InviteSentModal } from './InviteSentModal.js';
import { rescheduleConfirmText } from './rescheduleConfirmText.js';
import { getTimezoneShortName } from '../../../components/scheduling/slotPickerUtils.js';

// ============================================================================
// Types
// ============================================================================

// MED-1365: 'invite-sent' follows each booking and reschedule.
type ModalStep = 'select-date' | 'confirm' | 'invite-sent';

// ============================================================================
// Helpers
// ============================================================================

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

function formatConfirmDate(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: tz,
  });
}

function formatConfirmTime(isoTime: string, tz: string): string {
  return new Date(isoTime).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: tz,
  });
}

// ============================================================================
// Props
// ============================================================================

interface ScheduleSessionModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  session: ParticipantOneOnOneSession;
  totalSessions: number;
  coach: ParticipantOneOnOneCoach | null;
  cohortName: string;
  defaultMeetingLink?: string | null;
  /** When true, this is a reschedule (cancel + rebook). Modal title and API call change. */
  rescheduleMode?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function ScheduleSessionModal({
  opened,
  onClose,
  onSuccess,
  session,
  totalSessions,
  coach,
  cohortName,
  defaultMeetingLink: _defaultMeetingLink,
  rescheduleMode = false,
}: ScheduleSessionModalProps) {
  const { user } = useAuthStore();
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  // MED-1319: phones and small tablets get a full-screen modal with the
  // picker stacked under the coach info.
  const stacked = useMediaQuery('(max-width: 62em)', false, { getInitialValueInEffect: false });

  // Selection state
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<CalendlyAvailableSlot | null>(null);
  const [step, setStep] = useState<ModalStep>('select-date');
  // MED-1365: true when Medley sent a Google invite (calendly_fallback).
  const [sentFallback, setSentFallback] = useState(false);

  // Confirm form
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const durationMinutes = session.duration_minutes ?? 60;
  const sessionNum = session.session_number ?? 1;
  // MED-1365 QA round 4: a reschedule names its session, so the times keep
  // the session's own slot.
  const movingId = rescheduleMode ? session.id : undefined;

  // Availability for the visible month, fetched in 7-day windows.
  const availability = useMonthAvailability<CalendlyAvailableSlot>({
    enabled: opened && !!coach?.id && !!coach?.calendly_connected,
    sourceKey: `${coach?.id ?? ''}:${movingId ?? ''}`,
    timezone,
    fetchWindow: (start, end) =>
      participantPortalApi.getCoachAvailability(
        coach?.id ?? '',
        start,
        end,
        durationMinutes,
        movingId
      ),
    timeoutMs: 15000,
  });
  const { isLoading: isLoadingSlots, slotsByDate, reset: resetAvailability } = availability;
  const availabilityFailed = availability.failure != null;

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setSelectedDate(null);
      setSelectedSlot(null);
      setStep('select-date');
      setNotes('');
      setError(null);
      resetAvailability();
    }
  }, [opened, resetAvailability]);

  // Handle day click
  const handleDayClick = (dateKey: string) => {
    setSelectedDate(dateKey);
    setSelectedSlot(null);
  };

  // Handle slot click
  const handleSlotClick = (slot: CalendlyAvailableSlot) => {
    setSelectedSlot(slot);
  };

  // Handle "Next" → go to confirm step
  const handleNext = () => {
    if (!selectedSlot) return;
    setStep('confirm');
    setError(null);
  };

  // Handle back from confirm
  const handleBack = () => {
    setStep('select-date');
    setError(null);
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedSlot) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const request = {
        scheduled_start: selectedSlot.start_time,
        timezone,
        notes: notes.trim() || undefined,
      };
      const result = rescheduleMode
        ? await participantPortalApi.rescheduleSession(session.id, request)
        : await participantPortalApi.scheduleSession(session.id, request);
      // MED-1365: tell the participant where the invite comes from. The
      // dashboard refreshes when they close the confirmation.
      setSentFallback(!!result.calendly_fallback);
      setStep('invite-sent');
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : rescheduleMode
            ? 'Failed to reschedule session. Please try again.'
            : 'Failed to schedule session. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // Render: No Calendly connected (AC21)
  // ============================================================================

  if (opened && coach && !coach.calendly_connected) {
    return (
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Text fw={700} size="lg">
            Schedule 1:1 Session
          </Text>
        }
        size="sm"
        radius="lg"
      >
        <Stack gap="md" py="md">
          <Text size="sm" c="dimmed" ta="center">
            Your coach hasn&apos;t set up scheduling yet. Please contact them directly.
          </Text>
          {coach.email && (
            <Text size="sm" fw={500} ta="center">
              {coach.email}
            </Text>
          )}
          <Button variant="outline" color="gray" onClick={onClose} fullWidth>
            Close
          </Button>
        </Stack>
      </Modal>
    );
  }

  // ============================================================================
  // Render: Invite sent (MED-1365)
  // ============================================================================

  if (step === 'invite-sent' && selectedSlot) {
    return (
      <InviteSentModal
        opened={opened}
        onDone={() => {
          onSuccess();
          onClose();
        }}
        heading={rescheduleMode ? 'Session rescheduled' : 'Session booked'}
        sessionTitle={`1:1 Coaching - Session ${sessionNum}`}
        dateText={formatConfirmDate(selectedSlot.start_time, timezone)}
        startTime={selectedSlot.start_time}
        timeText={formatConfirmTime(selectedSlot.start_time, timezone)}
        durationMinutes={durationMinutes}
        timezone={timezone}
        coachName={
          coach ? `${coach.first_name} ${coach.last_name}` : 'Your Coach'
        }
        email={user?.email ?? ''}
        fallback={sentFallback}
      />
    );
  }

  // ============================================================================
  // Render: Confirm Step (Step 3)
  // ============================================================================

  if (step === 'confirm' && selectedSlot) {
    const coachName = coach
      ? `${coach.first_name} ${coach.last_name}`
      : 'Your Coach';

    return (
      <Modal
        opened={opened}
        onClose={onClose}
        size="md"
        radius="lg"
        withCloseButton={false}
        padding={0}
      >
        {/* Header */}
        <Group justify="space-between" px="lg" py="md" style={{ borderBottom: '1px solid #E8E6E1' }}>
          <Group gap="xs">
            <ActionIcon variant="subtle" color="gray" onClick={handleBack} size="sm">
              <ArrowLeft size={16} />
            </ActionIcon>
            <Text fw={700} size="lg">
              {rescheduleMode ? 'Confirm reschedule' : 'Confirm your session'}
            </Text>
          </Group>
          <ActionIcon variant="subtle" color="gray" onClick={onClose} size="sm" aria-label="Close">
            <X size={16} />
          </ActionIcon>
        </Group>

        <Stack gap="md" px="lg" py="md">
          {/* Summary card */}
          <Box
            p="md"
            style={{
              backgroundColor: '#F5F3EF',
              borderRadius: 8,
              border: '1px solid #E8E6E1',
            }}
          >
            <Text fw={600} size="sm" mb="xs">
              1:1 Coaching - Session {sessionNum}
            </Text>
            <Stack gap={6}>
              <Group gap={8} wrap="nowrap">
                <CalendarIcon size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  {formatConfirmDate(selectedSlot.start_time, timezone)}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Clock size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  <Text span fw={600} c="dark">
                    {formatConfirmTime(selectedSlot.start_time, timezone)}
                  </Text>
                  {' '}&middot; {durationMinutes} min &middot;{' '}
                  {getTimezoneShortName(
                    timezone,
                    new Date(selectedSlot.start_time)
                  )}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <User size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  {coachName}
                  {coach?.job_title ? ` \u00B7 ${coach.job_title}` : ''}
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Monitor size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  Meeting link will be included
                </Text>
              </Group>
            </Stack>
          </Box>

          {/* Your Name (read-only) */}
          <TextInput
            label="Your Name"
            value={user ? `${user.first_name} ${user.last_name}` : ''}
            readOnly
            styles={{
              input: { backgroundColor: '#F5F3EF', borderColor: '#E8E6E1', color: '#1A1A1A' },
            }}
          />

          {/* Your Email (read-only) */}
          <TextInput
            label="Your Email"
            value={user?.email ?? ''}
            readOnly
            styles={{
              input: { backgroundColor: '#F5F3EF', borderColor: '#E8E6E1', color: '#1A1A1A' },
            }}
          />

          {/* Notes for coach */}
          <Textarea
            label={
              <Group gap={4}>
                <Text size="sm" fw={500}>Notes for your coach</Text>
                <Text size="sm" c="dimmed">(optional)</Text>
              </Group>
            }
            value={notes}
            onChange={e => setNotes(e.currentTarget.value)}
            placeholder="Anything you'd like to discuss or prepare for..."
            minRows={3}
            autosize
            styles={{
              input: { borderColor: '#E8E6E1' },
            }}
          />

          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}

          {/* CTA */}
          <Button
            fullWidth
            size="md"
            loading={isSubmitting}
            onClick={handleSubmit}
            style={{ backgroundColor: '#2D4739', border: 'none' }}
          >
            {rescheduleMode ? 'Reschedule & Send Invite' : 'Schedule & Send Invite'}
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            {rescheduleMode
              ? rescheduleConfirmText('session', session.scheduling_method, coachName)
              : `A calendar invite will be sent to you and ${coachName}.`}
          </Text>
        </Stack>
      </Modal>
    );
  }

  // ============================================================================
  // Render: Select Date & Time (Steps 1 & 2)
  // ============================================================================

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={932}
      fullScreen={stacked}
      radius={stacked ? 0 : 'lg'}
      withCloseButton={false}
      padding={0}
    >
      {/* Close button */}
      <ActionIcon
        variant="subtle"
        color="gray"
        onClick={onClose}
        style={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}
        size={stacked ? 44 : 'sm'}
        aria-label="Close"
      >
        <X size={16} />
      </ActionIcon>

      <Flex direction={stacked ? 'column' : 'row'} wrap="nowrap" align="stretch" style={{ minHeight: stacked ? undefined : 520 }}>
        {/* ===== LEFT PANEL: Coach Info ===== */}
        <Box
          p="lg"
          style={{
            width: stacked ? 'auto' : 240,
            minWidth: stacked ? 0 : 240,
            borderRight: stacked ? 'none' : '1px solid #E8E6E1',
            borderBottom: stacked ? '1px solid #E8E6E1' : 'none',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            {/* Coach avatar */}
            <Avatar size={48} radius="xl" color="medleyGreen" variant="filled" mb="sm">
              {coach ? getInitials(coach.first_name, coach.last_name) : '?'}
            </Avatar>
            {coach && (
              <Text size="xs" c="dimmed" mb={2}>
                {coach.first_name} {coach.last_name}
              </Text>
            )}
            <Text fw={700} size="lg" mb="md">
              1:1 Coaching Session
            </Text>

            {/* Session details */}
            <Stack gap={8}>
              <Group gap={8} wrap="nowrap">
                <Clock size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  {durationMinutes} min
                </Text>
              </Group>
              <Group gap={8} wrap="nowrap">
                <Monitor size={14} color="#888" />
                <Text size="sm" c="dimmed">
                  Video meeting
                </Text>
              </Group>
            </Stack>
          </Box>

          {/* Session counter chip */}
          <Box
            mt="xl"
            p="xs"
            style={{
              backgroundColor: '#F5F3EF',
              borderRadius: 8,
              borderLeft: '3px solid #E8E6E1',
            }}
          >
            <Text size="xs" c="dimmed">
              {buildOneOnOneSessionTitle(session.one_on_one_type, sessionNum, totalSessions)} for{' '}
              {cohortName}
            </Text>
          </Box>
        </Box>

        {/* On phones the picker needs its own padding; on desktop its two
          panels stay direct children of the row. */}
        <Box p={stacked ? 'lg' : undefined} style={{ display: stacked ? 'block' : 'contents' }}>
          <SlotPicker
            layout={stacked ? 'stacked' : 'panels'}
            availability={availability}
            timezone={timezone}
            selectedDate={selectedDate}
            onSelectDate={handleDayClick}
            onClearSelection={() => {
              setSelectedDate(null);
              setSelectedSlot(null);
            }}
            selectedSlot={selectedSlot}
            onSelectSlot={handleSlotClick}
            onNext={handleNext}
            topStatus={
              !isLoadingSlots && availabilityFailed && (
                <AvailabilityFailureAlert onRetry={availability.reload} />
              )
            }
            status={
              <>
                {/* No availability after loading */}
                {!isLoadingSlots && !availabilityFailed && slotsByDate.size === 0 && coach?.calendly_connected && (
                  <Text size="sm" c="dimmed" ta="center" mt="md">
                    No available times this month. Try navigating to the next month.
                  </Text>
                )}
              </>
            }
          />
        </Box>
      </Flex>
    </Modal>
  );
}
